import React, { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const MATRIX_SHOPPING_URL = "https://wa.me/5926665489";
const DEFAULT_WA_MESSAGE = "I was referred to by {name} on ios.matrixshopping.com\nI would like to shop online and save money";

function slugify(s) {
  return (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function NewLink() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [destination, setDestination] = useState(MATRIX_SHOPPING_URL);
  const [discount, setDiscount] = useState("shop, save, ship");
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const previewSlug = slugify(user?.name || "you") + "-XXXXXX";
  const isWhatsApp = /^https?:\/\/(www\.)?(wa\.me|api\.whatsapp\.com)\//i.test(destination);
  const effectiveMessage = (whatsappMessage.trim() || DEFAULT_WA_MESSAGE).replaceAll("{name}", user?.name || "you");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/links", {
        name: name.trim(),
        destination_url: destination.trim(),
        discount_offer: discount.trim() || null,
        whatsapp_message: whatsappMessage.trim() || null,
      });
      toast.success("Link created");
      navigate(`/dashboard/links/${data.link_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create link");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="label-eyebrow text-[#002FA7]">Create</div>
        <h1 className="text-4xl tracking-tight font-black text-[#111827]">New affiliate link</h1>
        <p className="text-[#4B5563] mt-2">Your name <span className="font-bold text-[#111827]">{user?.name}</span> will be embedded in the slug, so customers see who's referring them.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <form onSubmit={onSubmit} className="lg:col-span-3 card-flat p-6 space-y-5" data-testid="new-link-form">
          <div>
            <label className="label-eyebrow text-[#4B5563]">Link name (internal)</label>
            <input data-testid="new-link-name-input" required className="input-base mt-2" placeholder="Matrix Shopping — Spring Promo" value={name} onChange={(e) => setName(e.target.value)} />
            <p className="text-xs text-[#9CA3AF] mt-1">Only you see this — used in your dashboard.</p>
          </div>
          <div>
            <label className="label-eyebrow text-[#4B5563]">Destination URL</label>
            <input data-testid="new-link-destination-input" required className="input-base mt-2 font-mono text-xs" placeholder="https://your-store.com/spring-sale" value={destination} onChange={(e) => setDestination(e.target.value)} />
            <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-[#9CA3AF]">Where the customer lands after they fill the popup.</p>
              <button
                type="button"
                onClick={() => setDestination(MATRIX_SHOPPING_URL)}
                data-testid="use-matrix-shopping-button"
                className="text-xs font-bold text-[#002FA7] hover:underline"
              >
                Use Matrix Shopping (WhatsApp)
              </button>
            </div>
            <div className="mt-2 rounded-md bg-[#F0FDF4] border border-[#BBF7D0] px-3 py-2 text-xs text-[#166534]" data-testid="matrix-shopping-hint">
              <span className="font-bold">Default:</span> Matrix Shopping on WhatsApp — every new link points here unless you change it.
            </div>
          </div>
          <div>
            <label className="label-eyebrow text-[#4B5563]">Discount offered</label>
            <input data-testid="new-link-discount-input" className="input-base mt-2" placeholder="shop, save, ship" value={discount} onChange={(e) => setDiscount(e.target.value)} />
            <p className="text-xs text-[#9CA3AF] mt-1">Shown on the popup. Customer clicks Yes/No.</p>
          </div>
          {isWhatsApp && (
            <div data-testid="whatsapp-message-block">
              <label className="label-eyebrow text-[#4B5563]">Pre-filled WhatsApp message <span className="text-[#9CA3AF] font-normal lowercase">(optional)</span></label>
              <textarea
                data-testid="new-link-whatsapp-message-input"
                rows={4}
                className="input-base mt-2 font-mono text-xs leading-relaxed"
                placeholder={DEFAULT_WA_MESSAGE}
                value={whatsappMessage}
                onChange={(e) => setWhatsappMessage(e.target.value)}
              />
              <p className="text-xs text-[#9CA3AF] mt-1">
                This is the message customers see pre-typed when they land on WhatsApp. Use <code className="font-mono bg-[#F3F4F6] px-1 rounded">{"{name}"}</code> to auto-insert your name. Leave blank to use the default.
              </p>
              <div className="mt-2 rounded-md bg-[#F9FAFB] border border-[#E5E7EB] px-3 py-2 text-xs text-[#111827] whitespace-pre-wrap font-mono" data-testid="whatsapp-message-preview">
                {effectiveMessage}
              </div>
            </div>
          )}
          <button type="submit" data-testid="new-link-submit" disabled={submitting} className="btn-primary w-full">{submitting ? "Creating…" : "Create link"}</button>
        </form>

        <aside className="lg:col-span-2 space-y-4">
          <div className="card-flat p-6">
            <div className="label-eyebrow text-[#4B5563] mb-2">Preview slug</div>
            <code className="font-mono text-sm bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-3 block break-all">{window.location.origin}/r/<span className="text-[#002FA7] font-bold">{previewSlug}</span></code>
            <p className="text-xs text-[#4B5563] mt-3">A unique 6-character suffix is generated to keep slugs unique across the platform.</p>
          </div>
          <div className="card-flat p-6">
            <div className="label-eyebrow text-[#002FA7]">What happens on click</div>
            <ol className="mt-3 space-y-2 text-sm text-[#4B5563] list-decimal list-inside">
              <li>Click is logged with timestamp + country (via IP).</li>
              <li>Customer sees a popup asking name, email, delivery location, discount yes/no.</li>
              <li>On submit, lead is captured and visitor is forwarded to your destination URL.</li>
            </ol>
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}
